// @ts-nocheck
import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import Map, { Source, Layer, NavigationControl, Popup } from "react-map-gl";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store";
import { setFlyLocation } from "../store/mapStateSlice";
import "mapbox-gl/dist/mapbox-gl.css";
import * as turf from "@turf/turf";
import useIsMobile from "../hooks/useIsMobile";
import { ReactComponent as MapIcon } from "../assets/icons/map-02.svg"
import { ReactComponent as GlobeIcon } from "../assets/icons/globe-02.svg"
import { ReactComponent as MarkerPin } from "../assets/icons/marker-pin-03.svg"
import { loadAssetIcons, getIconImageExpression } from "./AssetMapView/utils/iconUtils";

const mapBoxToken =
  "pk.eyJ1IjoiY3JhaWdtYWMwMDciLCJhIjoiY2xkbHNlZ3h1MDE2bjNxcGJjYWo5YzRtMCJ9.ezesyu3cASMlnZlJLMkZwg";

interface GeoJSONMapProps {
  assets: any[];
  defaultLat?: number;
  defaultLng?: number;
  onAssetClick?: (asset: any) => void;
  selectedAsset?: any;
  boundsPadding?: { mobile: number, desktop: number };
  /** Opt-in coordinate readout, for a host that dropped its lat/lng fields in favour of the map. */
  coordinates?: { lat?: number | string | null; lng?: number | string | null } | null;
}

const formatCoordinate = (value, digits = 5) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(digits) : null;

const GeoJSONMap: React.FC<GeoJSONMapProps> = ({
  assets = [],
  defaultLat = 53.51,
  defaultLng = -103.037,
  onAssetClick = () => {},
  selectedAsset = null,
  boundsPadding = { mobile: 25, desktop: 50 },
  coordinates = null
}) => {
  const dispatch = useDispatch();
  const mapState = useSelector((state: RootState) => state.mapState);
  const mapRef = useRef<any>(null);
  // No popup state needed
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [iconsVersion, setIconsVersion] = useState(0);
  // Track the last asset we've flown to
  const lastFlownAssetRef = useRef(null);
  const [viewState, setViewState] = useState({
    longitude: defaultLng,
    latitude: defaultLat,
    zoom: 4,
  });

  // Use our existing hook to check if we're on mobile
  const isMobile = useIsMobile();

  // Both halves must parse, or the readout would show a lone number with no axis
  const lat = formatCoordinate(coordinates?.lat);
  const lng = formatCoordinate(coordinates?.lng);
  const coordinateLabel = lat && lng ? `${lat}, ${lng}` : null;
  
  // Generate GeoJSON for all assets
  const generateAssetsGeoJSON = () => {
    if (!assets || assets.length === 0) return null;
    
    const features = assets.map((asset) => {
      // If asset has a geojson property and it's valid, use it
      if (asset.geojson) {
        try {
          // Parse the geojson string if needed
          let geojsonData = asset.geojson;
          
          if (typeof geojsonData === "string") {
            geojsonData = JSON.parse(geojsonData);
            
          }
          
          // Handle all possible GeoJSON structures
          if (geojsonData.type === "FeatureCollection" && geojsonData.features && geojsonData.features.length > 0) {
            // It's a FeatureCollection - extract the first feature
            return {
              type: "Feature",
              geometry: geojsonData.features[0].geometry,
              properties: {
                ...geojsonData.features[0].properties,
                id: asset.id,
                name: asset.name,
                description: asset.description,
                assetGroupId: asset.assetGroupId,
                assetOriginal: asset,
                ...(asset.icon ? { icon: asset.icon } : {}),
              },
            };
          } else if (geojsonData.type === "Feature") {
            // It's already a Feature, just add/update properties
            return {
              ...geojsonData,
              properties: {
                ...geojsonData.properties,
                id: asset.id,
                name: asset.name,
                description: asset.description,
                assetGroupId: asset.assetGroupId,
                assetOriginal: asset,
                ...(asset.icon ? { icon: asset.icon } : {}),
              },
            };
          } else if (["Point", "MultiPoint", "LineString", "Polygon"].includes(geojsonData.type)) {
            // It's a raw geometry, wrap it in a Feature
            return {
              type: "Feature",
              geometry: geojsonData,
              properties: {
                id: asset.id,
                name: asset.name,
                description: asset.description,
                assetGroupId: asset.assetGroupId,
                assetOriginal: asset,
                ...(asset.icon ? { icon: asset.icon } : {}),
              },
            };
          } else {
            console.error("Unknown GeoJSON structure:", geojsonData);
            return createPointFeature(asset);
          }
        } catch (error) {
          console.error("Error parsing GeoJSON data:", error, typeof asset.geojson);
          // Fallback to point if geojson is invalid
          return createPointFeature(asset);
        }
      } else {
        // Create a point feature from lat/lng
        return createPointFeature(asset);
      }
    });

    return {
      type: "FeatureCollection",
      features: features.filter(Boolean),
    };
  };

  const createPointFeature = (asset: any) => {
    // Handle cases where lat/lng might be strings or numbers
    let lat = asset.lat;
    let lng = asset.lng;
    
    // Ensure we have valid coordinates
    if (lat === undefined || lng === undefined) {
      console.error("Missing coordinates for asset:", asset.name, asset.id);
      return null;
    }
    
    // Convert to numbers if they're strings
    lat = Number(lat);
    lng = Number(lng);
    
    if (isNaN(lat) || isNaN(lng)) {
      console.error("Invalid coordinates for asset:", asset.name, asset.id, lat, lng);
      return null;
    }
    
    return {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [lng, lat],
      },
      properties: {
        id: asset.id,
        name: asset.name,
        description: asset.description,
        assetGroupId: asset.assetGroupId,
        assetOriginal: asset,
        ...(asset.icon ? { icon: asset.icon } : {}),
      },
    };
  };

  // Fly to a specific asset
  const flyToAsset = (asset: any) => {
    if (!asset || !mapRef.current) return;
    try {
      // Use the boundsPadding prop for configurable padding
      const padding = isMobile ? boundsPadding.mobile : boundsPadding.desktop;
      
      const mobileZoom = 5; 
      const desktopZoom = 7;
      const defaultZoom = isMobile ? mobileZoom : desktopZoom;
      
      // Handle assets with GeoJSON
      if (asset.geojson) {
        try {
          // Parse GeoJSON if it's a string
          let geojsonData = asset.geojson;
          if (typeof geojsonData === "string") {
            geojsonData = JSON.parse(geojsonData);
          }
          
          // Check if this is a single point or should be treated as one
          let isSinglePoint = false;
          let pointCoordinates = null;
          
          // Case 1: Direct Point geometry
          if (geojsonData.type === "Point") {
            isSinglePoint = true;
            pointCoordinates = geojsonData.coordinates;
          } 
          // Case 2: MultiPoint with just one point
          else if (geojsonData.type === "MultiPoint" && 
                  geojsonData.coordinates && 
                  geojsonData.coordinates.length === 1) {
            isSinglePoint = true;
            pointCoordinates = geojsonData.coordinates[0];
          } 
          // Case 3: Feature with Point geometry
          else if (geojsonData.type === "Feature" && geojsonData.geometry) {
            if (geojsonData.geometry.type === "Point") {
              isSinglePoint = true;
              pointCoordinates = geojsonData.geometry.coordinates;
            } 
            // Case 4: Feature with MultiPoint containing just one point
            else if (geojsonData.geometry.type === "MultiPoint" && 
                    geojsonData.geometry.coordinates && 
                    geojsonData.geometry.coordinates.length === 1) {
              isSinglePoint = true;
              pointCoordinates = geojsonData.geometry.coordinates[0];
            }
          } 
          // Case 5: FeatureCollection with just one Point feature
          else if (geojsonData.type === "FeatureCollection" && 
                  geojsonData.features && 
                  geojsonData.features.length === 1) {
            const feature = geojsonData.features[0];
            if (feature.geometry) {
              if (feature.geometry.type === "Point") {
                isSinglePoint = true;
                pointCoordinates = feature.geometry.coordinates;
              } 
              // Case 6: FeatureCollection with one feature that's a MultiPoint with one point
              else if (feature.geometry.type === "MultiPoint" && 
                      feature.geometry.coordinates && 
                      feature.geometry.coordinates.length === 1) {
                isSinglePoint = true;
                pointCoordinates = feature.geometry.coordinates[0];
              }
            }
          }
          
          // Handle single points with direct flyTo
          if (isSinglePoint && pointCoordinates) {
            mapRef.current.flyTo({
              center: [pointCoordinates[0], pointCoordinates[1]],
              zoom: defaultZoom,
              duration: 1500,
              essential: true
            });
            return;
          }
          
          // For all other complex geometries (MultiPoint with 2+ points, LineString, Polygon) Use fitBounds to show the entire feature
          let geomForBounds;
          
          if (geojsonData.type === "FeatureCollection") {
            geomForBounds = geojsonData;
          } else if (geojsonData.type === "Feature") {
            geomForBounds = geojsonData;
          } else {
            // Raw geometry (MultiPoint with multiple points, LineString, Polygon, etc.)
            geomForBounds = {
              type: "Feature",
              geometry: geojsonData
            };
          }
          
          // Calculate bounds and apply fitBounds
          const bbox = turf.bbox(geomForBounds);
          const bounds = [[bbox[0], bbox[1]], [bbox[2], bbox[3]]];
          
          // Check if bounds are valid (some area)
          const isValidBounds = 
            Math.abs(bounds[0][0] - bounds[1][0]) > 0.00001 || 
            Math.abs(bounds[0][1] - bounds[1][1]) > 0.00001;
            
          if (isValidBounds) {
            
            mapRef.current.fitBounds(bounds, {
              padding: padding,
              duration: 1500,
              essential: true
            });
          } else {
            // If bounds are too small (practically a point), use flyTo instead
            const centerLng = (bounds[0][0] + bounds[1][0]) / 2;
            const centerLat = (bounds[0][1] + bounds[1][1]) / 2;
            
            mapRef.current.flyTo({
              center: [centerLng, centerLat],
              zoom: defaultZoom,
              duration: 1500,
              essential: true
            });
          }
        } catch (error) {
          // Fallback to lat/lng if GeoJSON processing fails
          if (Number.isFinite(Number(asset.lat)) && Number.isFinite(Number(asset.lng))) {
            mapRef.current.flyTo({
              center: [Number(asset.lng), Number(asset.lat)],
              zoom: defaultZoom,
              duration: 1500,
              essential: true
            });
          }
        }
      } 
      // Handle assets with just lat/lng (no GeoJSON)
      else if (Number.isFinite(Number(asset.lat)) && Number.isFinite(Number(asset.lng))) {
        mapRef.current.flyTo({
          center: [Number(asset.lng), Number(asset.lat)],
          zoom: defaultZoom,
          duration: 1500,
          essential: true
        });
      }
    } catch (error) {
      console.error("Error flying to asset:", error);
      
      // Ultimate fallback if all else fails
      if (asset.lat && asset.lng) {
        mapRef.current.flyTo({
          center: [Number(asset.lng), Number(asset.lat)],
          zoom: isMobile ? 6 : 8,
          duration: 1500,
          essential: true
        });
      }
    }
  };

  // Handle click on map features
  const handleClick = (event: any) => {
    const features = event.features || [];
    
    if (features.length > 0) {
      const clickedFeature = features[0];
      const assetId = clickedFeature.properties.id;
      
      // Call the provided click handler with the asset ID
      if (onAssetClick) {
        onAssetClick(assetId);
      }
      
      // Set the selected asset ID for highlighting
      setSelectedAssetId(assetId);
    } else {
      // Clear selection if clicking on empty area
      setSelectedAssetId(null);
    }
  };

  // Fly to selected asset when it changes
  useEffect(() => {
    // Only proceed if map and asset are both available
    if (mapRef.current && selectedAsset) {
      
      // Check if this is a new asset to fly to
      const isNewAsset = !lastFlownAssetRef.current || lastFlownAssetRef.current.id !== selectedAsset.id;
      
      // Always update the selected asset ID for highlighting
      if (selectedAsset.id) {
        setSelectedAssetId(selectedAsset.id);
      }
      
      // Only fly to the asset if it's new or we're forcing a fly
      if (isNewAsset) {
        
        // Try our custom flyToAsset first
        flyToAsset(selectedAsset);
        
        // As an extra fallback, also set the map's viewport directly This guarantees we at least move the map to the right area
        if (selectedAsset.lat && selectedAsset.lng) {
          setViewState({
            longitude: Number(selectedAsset.lng),
            latitude: Number(selectedAsset.lat),
            zoom: isMobile ? 5 : 5,
          });
        }
        
        // Update the ref to track that we've flown to this asset
        lastFlownAssetRef.current = selectedAsset;
      }
    }
  }, [selectedAsset])

  // Reference to the map container for dimension calculations
  const mapContainerRef = useRef<HTMLDivElement>(null);
  
  // We no longer need to adjust map height dynamically as it's fixed by the parent container
  useLayoutEffect(() => {
    // No dynamic height adjustment needed, the map will fill its container which is strictly limited to 25% of the screen
    return () => {};
  }, []);
  
  // Update interactiveLayerIds to include all layer types
  const interactiveLayerIds = React.useMemo(() => [
    "assets-fill", "assets-line", "assets-points",
    "assets-fill-selected", "assets-line-selected", "assets-points-selected",
    "assets-icons-fill", "assets-icons-stroke",
    "assets-labels"
  ], []);
  
  // Generate asset GeoJSON data and use memoization to prevent unnecessary recalculation
  const assetGeoJSON = React.useMemo(() => {
    return generateAssetsGeoJSON();
  }, [assets]); // Only regenerate when assets change
  
  // Generate a separate GeoJSON for labels that handles MultiPoint features specially
  const labelGeoJSON = React.useMemo(() => {
    if (!assets || assets.length === 0) return null;
    
    // First create a copy of the asset GeoJSON
    const assetData = generateAssetsGeoJSON();
    if (!assetData) return null;
    
    // Create a new feature collection for labels
    const labelFeatures = assetData.features.map(feature => {
      // For MultiPoint geometries, create a single Point at the first coordinate
      if (feature.geometry.type === "MultiPoint" && feature.geometry.coordinates && feature.geometry.coordinates.length > 0) {
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            // Use the first point's coordinates
            coordinates: feature.geometry.coordinates[0]
          },
          properties: feature.properties
        };
      }
      // For all other geometries, use the original feature
      return feature;
    });
    
    return {
      type: "FeatureCollection",
      features: labelFeatures
    };
  }, [assets]);

  // State for map style
  const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/satellite-streets-v12"); // Default satellite style

  // console.log("assetGeoJSON", assetGeoJSON)
  return (
    <div ref={mapContainerRef} className="eai-media relative h-full w-full">
      <Map
        ref={mapRef}
        mapboxAccessToken={mapBoxToken}
        // mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN || ""}
        mapStyle={mapStyle}
        interactiveLayerIds={interactiveLayerIds}
        {...viewState}
        onMove={(evt) => setViewState(evt.viewState)}
        onClick={handleClick}
        onLoad={() => {
          // Load SDF icons for custom asset markers
          if (mapRef.current) {
            try {
              const mapInstance = mapRef.current.getMap();
              loadAssetIcons(mapInstance);
              setIconsVersion(v => v + 1);
              // Re-load icons after style changes (style swap wipes custom images)
              mapInstance.on("style.load", () => {
                loadAssetIcons(mapInstance);
                setIconsVersion(v => v + 1);
              });
            } catch (e) {
              console.error("[GeoJSONMap] Error loading asset icons:", e);
            }
          }
          // When map loads, make sure to fly to the selected asset if available
          if (selectedAsset) {
            flyToAsset(selectedAsset);
            // Update the ref so we don't fly again in the useEffect
            lastFlownAssetRef.current = selectedAsset;
          }
        }}
      >
        {/* <NavigationControl position="top-right" /> */}
        
        {assetGeoJSON && (
          <Source id="assets-source" type="geojson" data={assetGeoJSON}>
            {/* Polygons - Regular */}
            <Layer
              id="assets-fill"
              type="fill"
              paint={{
                "fill-color": "#A2BBBF",
                "fill-opacity": 0.2,
              }}
              filter={[
                "all",
                ["==", "$type", "Polygon"],
                ["!=", "id", selectedAsset.id || ""]
              ]}
            />
            
            {/* Polygons - Selected (highlighted in yellow) */}
            <Layer
              id="assets-fill-selected"
              type="fill"
              paint={{
                "fill-color": "#FFCC00", 
                "fill-opacity": 0.5,
              }}
              filter={[
                "all",
                ["==", "$type", "Polygon"],
                ["==", "id", selectedAsset.id || ""]
              ]}
            />
            
            {/* Lines - Regular */}
            <Layer
              id="assets-line"
              type="line"
              paint={{
                "line-color": "#A2BBBF",
                "line-width": 2,
              }}
              filter={[
                "all",
                ["in", "$type", "LineString", "Polygon"],
                ["!=", "id", selectedAsset.id || ""]
              ]}
            />
            
            {/* Lines - Selected (highlighted in yellow) */}
            <Layer
              id="assets-line-selected"
              type="line"
              paint={{
                "line-color": "#FFCC00",
                "line-width": 3,
              }}
              filter={[
                "all",
                ["in", "$type", "LineString", "Polygon"],
                ["==", "id", selectedAsset.id || ""]
              ]}
            />
            
            {/* Points - Regular (no custom icon) */}
            <Layer
              id="assets-points"
              type="circle"
              paint={{
                "circle-radius": 6,
                "circle-color": "#A2BBBF",
                "circle-stroke-width": 2,
                "circle-stroke-color": "#000000",
              }}
              filter={[
                "all",
                ["==", "$type", "Point"],
                ["!=", "id", selectedAsset.id || ""],
                ["!has", "icon"]
              ]}
            />
            
            {/* MultiPoint features - Selected - keep same size, just change color */}
            <Layer
              id="assets-points-selected-multi"
              type="circle"
              paint={{
                "circle-radius": 6,
                "circle-color": "#FFCC00",
                "circle-stroke-width": 2,
                "circle-stroke-color": "#000000",
              }}
              filter={[
                "all",
                ["==", "$type", "Point"],
                ["==", "id", selectedAsset.id || ""],
                ["==", "geometry.type", "MultiPoint"],
                ["!has", "icon"]
              ]}
            />
            {/* Points - Selected (highlighted in yellow with increased size) - ONLY for non-MultiPoint */}
            <Layer
              id="assets-points-selected"
              type="circle"
              paint={{
                "circle-radius": 6,
                "circle-color": "#FFCC00",
                "circle-stroke-width": 2,
                "circle-stroke-color": "#000000",
              }}
              filter={[
                "all",
                ["==", "$type", "Point"],
                ["==", "id", selectedAsset.id || ""],
                ["!=", "geometry.type", "MultiPoint"],
                ["!has", "icon"]
              ]}
            />

            {/* Custom icon fill — tinted with asset color */}
            {iconsVersion > 0 && (
              <Layer
                key={`assets-icons-fill-${iconsVersion}`}
                id="assets-icons-fill"
                type="symbol"
                layout={{
                  "icon-image": getIconImageExpression("fill"),
                  "icon-size": 0.8,
                  "icon-allow-overlap": true,
                }}
                paint={{
                  "icon-color": [
                    "case",
                    ["==", ["get", "id"], selectedAsset?.id || ""],
                    "#FFCC00",
                    "#A2BBBF"
                  ],
                }}
                filter={[
                  "all",
                  ["==", "$type", "Point"],
                  ["has", "icon"]
                ]}
              />
            )}

            {/* Custom icon stroke — dark outline */}
            {iconsVersion > 0 && (
              <Layer
                key={`assets-icons-stroke-${iconsVersion}`}
                id="assets-icons-stroke"
                type="symbol"
                layout={{
                  "icon-image": getIconImageExpression("stroke"),
                  "icon-size": 0.8,
                  "icon-allow-overlap": true,
                }}
                paint={{
                  "icon-color": "#1a1a1a",
                }}
                filter={[
                  "all",
                  ["==", "$type", "Point"],
                  ["has", "icon"]
                ]}
              />
            )}
          </Source>
        )}
        
        {/* Separate source for labels to handle MultiPoint features properly */}
        {labelGeoJSON && (
          <Source id="label-source" type="geojson" data={labelGeoJSON}>
            <Layer
              id="assets-labels"
              type="symbol"
              layout={{
                "text-field": ["get", "name"],
                "text-size": 12,
                "text-offset": [0, 1.5],
                "text-anchor": "top",
              }}
              paint={{
                "text-color": "#333",
                "text-halo-color": "#fff",
                "text-halo-width": 1,
              }}
            />
          </Source>
        )}
        
        {/* Coordinate readout — translucent and borderless on purpose, so it reads as a map label rather than another control like the style toggle opposite it */}
        {coordinateLabel && (
          <div className="pointer-events-none absolute left-2 top-2 z-10 inline-flex items-center gap-1.5 rounded-eai-card bg-eai-surface-card/85 px-2 py-1 font-inter text-eai-body-xs text-eai-ink-muted backdrop-blur-sm">
            <MarkerPin className="h-3 w-3 shrink-0 [&_path]:stroke-current" />
            {coordinateLabel}
          </div>
        )}

        {/* Map Style Toggle */}
        <div className="map-controls absolute top-2 right-2 z-10">
          <div className="flex overflow-hidden rounded-eai-card border border-eai-border bg-eai-surface-control shadow-eai-card">
            <button
              className={`p-1 ${mapStyle === "mapbox://styles/mapbox/light-v11" ? "bg-eai-surface-active" : "bg-eai-surface-control hover:bg-eai-surface-hover"}`}
              onClick={() => setMapStyle("mapbox://styles/mapbox/light-v11")}
              title="Map View"
            >
              <MapIcon className="w-6 h-6" />
            </button>
            <button
              className={`p-1 ${mapStyle === "mapbox://styles/mapbox/satellite-streets-v12" ? "bg-eai-surface-active" : "bg-eai-surface-control hover:bg-eai-surface-hover"}`}
              onClick={() => setMapStyle("mapbox://styles/mapbox/satellite-streets-v12")}
              title="Satellite View"
            >
              <GlobeIcon className="w-6 h-6" />
            </button>
          </div>
        </div>
        {/* No popup */}
      </Map>
    </div>
  );
};

export default GeoJSONMap;
